import { useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import './RichText.css';

interface RichTextProps {
  content: string;
  className?: string;
  customFonts?: {
    heading?: string;
    body?: string;
    code?: string;
  };
}

/**
 * RichText component for rendering CMS content
 * Supports Markdown, HTML, and custom fonts
 */
export function RichText({ content, className = '', customFonts }: RichTextProps): JSX.Element {
  // Load custom fonts dynamically
  useEffect(() => {
    if (customFonts) {
      const fontsToLoad: string[] = [];
      
      if (customFonts.heading) {
        fontsToLoad.push(customFonts.heading);
      }
      if (customFonts.body) {
        fontsToLoad.push(customFonts.body);
      }
      if (customFonts.code) {
        fontsToLoad.push(customFonts.code);
      }

      // Load Google Fonts
      fontsToLoad.forEach((font) => {
        if (font && !document.querySelector(`link[href*="${font}"]`)) {
          const link = document.createElement('link');
          link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@300;400;500;600;700;800;900&display=swap`;
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
      });
    }
  }, [customFonts]);

  // Generate CSS for custom fonts
  const fontStyles = useMemo(() => {
    if (!customFonts) return {};
    
    return {
      '--font-heading': customFonts.heading ? `'${customFonts.heading}', sans-serif` : undefined,
      '--font-body': customFonts.body ? `'${customFonts.body}', sans-serif` : undefined,
      '--font-code': customFonts.code ? `'${customFonts.code}', monospace` : undefined,
    } as React.CSSProperties;
  }, [customFonts]);

  return (
    <div
      className={`rich-text-content ${className}`}
      style={fontStyles}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-4xl font-bold mb-4 mt-6" style={{ fontFamily: 'var(--font-heading, inherit)' }}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-3xl font-bold mb-3 mt-5" style={{ fontFamily: 'var(--font-heading, inherit)' }}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-2xl font-semibold mb-2 mt-4" style={{ fontFamily: 'var(--font-heading, inherit)' }}>
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-xl font-semibold mb-2 mt-3" style={{ fontFamily: 'var(--font-heading, inherit)' }}>
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className="mb-4 leading-relaxed" style={{ fontFamily: 'var(--font-body, inherit)' }}>
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside mb-4 space-y-2 ml-4" style={{ fontFamily: 'var(--font-body, inherit)' }}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-4 space-y-2 ml-4" style={{ fontFamily: 'var(--font-body, inherit)' }}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="mb-1">{children}</li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-purple-500 pl-4 italic my-4 text-gray-700 dark:text-gray-300" style={{ fontFamily: 'var(--font-body, inherit)' }}>
              {children}
            </blockquote>
          ),
          code: ({ children, className }) => {
            const isInline = !className;
            return isInline ? (
              <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono" style={{ fontFamily: 'var(--font-code, monospace)' }}>
                {children}
              </code>
            ) : (
              <code className={className} style={{ fontFamily: 'var(--font-code, monospace)' }}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto mb-4" style={{ fontFamily: 'var(--font-code, monospace)' }}>
              {children}
            </pre>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              className="text-purple-600 dark:text-purple-400 hover:underline"
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt}
              className="max-w-full h-auto rounded-lg my-4"
              loading="lazy"
            />
          ),
          hr: () => <hr className="my-6 border-gray-300 dark:border-gray-700" />,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

